import { useCallback, useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { AccountPage } from "./components/AccountPage";
import { AppShell, type NavId } from "./components/AppShell";
import { SettingsPage } from "./components/SettingsPage";
import { Dashboard } from "./components/Dashboard";
import { SubscriptionTable } from "./components/SubscriptionTable";
import { SubscriptionDialog } from "./components/SubscriptionDialog";
import { UpdateChecker } from "./components/UpdateChecker";
import {
  clearSubscriptionsLocalCache,
  consumeLogoutGuestMarker,
  deleteSubscription,
  getSubscriptionsSnapshot,
  initSubscriptionStore,
  insertSubscription,
  setSubscriptionActive,
  syncSubscriptionsWithCloudIfLinked,
  updateSubscription,
} from "./lib/db";
import { clearUserSettingsLocalCache } from "./lib/user-settings";
import { syncUserSettingsWithCloudIfLinked } from "./lib/settings-cloud";
import { setCloudSessionActive } from "./lib/cloud-session";
import type { Subscription, SubscriptionInput } from "./types/subscription";
import { initAnalytics, trackPageView } from "./lib/analytics";
import { useAppAuth } from "./lib/auth-context";
import { getDesktopDownloadUrl } from "./lib/desktop-download";
import { consumeInitialNavFromAuthRedirect } from "./lib/postLoginNav";
import {
  getNavFromLocationHash,
  syncLocationHashToNav,
} from "./lib/nav-url";
import { isAuth0Configured } from "./lib/api/config";
import {
  hasWelcomeContinued,
  setWelcomeContinuedAsGuest,
} from "./lib/welcome-choice";
import { WelcomeGate } from "./components/WelcomeGate";

export default function App() {
  const { isAuthenticated, isLoading, ensureApiSession } = useAppAuth();
  const [guestContinued, setGuestContinued] = useState(() =>
    hasWelcomeContinued(),
  );
  const [nav, setNav] = useState<NavId>(
    () =>
      consumeInitialNavFromAuthRedirect() ??
      getNavFromLocationHash() ??
      "dashboard",
  );

  useEffect(() => {
    syncLocationHashToNav(nav);
  }, [nav]);

  useEffect(() => {
    const onHashChange = () => {
      const next = getNavFromLocationHash();
      if (next) {
        setNav((prev) => (next === prev ? prev : next));
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editTarget, setEditTarget] = useState<Subscription | null>(null);
  /** Tracks prior signed-in state so we detect logout and drop cached cloud rows from this browser. */
  const wasAuthenticatedRef = useRef(false);

  const prevNav = useRef(nav);
  useEffect(() => {
    initAnalytics();
    trackPageView(`/${nav}`, nav);
  }, []);
  useEffect(() => {
    if (prevNav.current !== nav) {
      prevNav.current = nav;
      trackPageView(`/${nav}`, nav);
    }
  }, [nav]);

  const refresh = useCallback(async () => {
    try {
      setLoadError(null);
      consumeLogoutGuestMarker();
      try {
        await initSubscriptionStore();
      } catch (e) {
        console.warn(e);
      }
      if (!isLoading) {
        if (wasAuthenticatedRef.current && !isAuthenticated) {
          clearSubscriptionsLocalCache();
          clearUserSettingsLocalCache();
          setCloudSessionActive(false);
        }
        wasAuthenticatedRef.current = isAuthenticated;
      }
      await syncSubscriptionsWithCloudIfLinked({
        isAuthenticated,
        isLoading,
        ensureApiSession,
      });
      await syncUserSettingsWithCloudIfLinked({
        isAuthenticated,
        isLoading,
        ensureApiSession,
      });
      setSubscriptions(getSubscriptionsSnapshot());
    } catch (e) {
      setLoadError(
        e instanceof Error ? e.message : "Could not load subscription data.",
      );
    }
  }, [isAuthenticated, isLoading, ensureApiSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function openCreate() {
    setDialogMode("create");
    setEditTarget(null);
    setDialogOpen(true);
  }

  function openEdit(s: Subscription) {
    setDialogMode("edit");
    setEditTarget(s);
    setDialogOpen(true);
  }

  async function handleSave(input: SubscriptionInput) {
    if (dialogMode === "edit" && editTarget) {
      await updateSubscription(editTarget.id, input);
    } else {
      await insertSubscription(input);
    }
    setSubscriptions(getSubscriptionsSnapshot());
  }

  async function handleDelete(s: Subscription) {
    await deleteSubscription(s.id);
    setSubscriptions(getSubscriptionsSnapshot());
  }

  async function handleToggle(s: Subscription, active: boolean) {
    await setSubscriptionActive(s.id, active);
    setSubscriptions(getSubscriptionsSnapshot());
  }

  const desktopDownloadUrl = getDesktopDownloadUrl();

  const showWelcomeGate =
    isAuth0Configured() &&
    !isLoading &&
    !isAuthenticated &&
    !guestContinued;

  const pageTitle: Record<NavId, string> = {
    dashboard: "Overview",
    subscriptions: "All subscriptions",
    settings: "Settings",
    account: "Account",
  };

  if (isAuth0Configured() && isLoading && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-0">
        <p className="text-sm text-text-muted">Loading…</p>
      </div>
    );
  }

  return (
    <AppShell active={nav} onNavigate={setNav}>
      <>
        {showWelcomeGate && (
          <WelcomeGate
            onContinueGuest={() => {
              setWelcomeContinuedAsGuest();
              setGuestContinued(true);
            }}
          />
        )}
        <UpdateChecker />
        <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-2xl font-bold tracking-tight text-text sm:text-3xl md:text-4xl">
              {pageTitle[nav]}
            </h1>
            {nav === "dashboard" && desktopDownloadUrl && (
              <p className="mt-2 hidden text-sm md:block">
                <a
                  href={desktopDownloadUrl}
                  rel="noopener noreferrer"
                  className="font-medium text-accent hover:underline"
                >
                  Download for Windows
                </a>
                <span className="text-text-muted"> — desktop app</span>
              </p>
            )}
          </div>
          {nav !== "account" && nav !== "settings" && (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex min-h-11 w-full shrink-0 items-center justify-center gap-2 touch-manipulation rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-surface-0 shadow-lg shadow-accent/25 hover:brightness-110 sm:w-auto"
            >
              <Plus className="h-4 w-4" />
              Add subscription
            </button>
          )}
        </div>

        {loadError && (
          <div
            className="mb-6 rounded-xl border border-danger/40 bg-surface-1 px-4 py-3 text-sm text-danger"
            role="alert"
          >
            {loadError}
          </div>
        )}

        {nav === "dashboard" ? (
          <Dashboard subscriptions={subscriptions} />
        ) : nav === "subscriptions" ? (
          <SubscriptionTable
            subscriptions={subscriptions}
            onEdit={openEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggle}
          />
        ) : nav === "settings" ? (
          <SettingsPage />
        ) : (
          <AccountPage />
        )}
      </div>

      <SubscriptionDialog
        open={dialogOpen && nav !== "account" && nav !== "settings"}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        initial={editTarget}
        onSubmit={handleSave}
      />
      </>
    </AppShell>
  );
}
